import { useEffect, useState } from "react";
import { Users, Mail, Copy, FileDown, Trash2 } from "lucide-react";
import { api, API, eur, fmtError } from "../lib/api";
import { toast } from "sonner";

export const AdminClients = () => {
  const [clients, setClients] = useState(null);

  const load = () =>
    api.get("/admin/clients").then((r) => setClients(r.data)).catch(() => setClients([]));

  useEffect(() => {
    load();
  }, []);

  if (!clients) return null;

  const copyAllEmails = () => {
    navigator.clipboard.writeText(clients.map((c) => c.email).join("\n"));
    toast.success(`${clients.length} adresse(s) copiée(s)`);
  };

  const deleteClient = async (c) => {
    if (!window.confirm(`Supprimer définitivement le compte de ${c.name || c.email} ? Cette action est irréversible.`)) return;
    try {
      await api.delete(`/admin/clients/${c.user_id}`);
      toast.success(`${c.name || c.email} supprimé définitivement`);
      load();
    } catch (e) {
      toast.error(fmtError(e));
    }
  };

  return (
    <section className="border border-white/[0.08] bg-surface p-8" data-testid="admin-clients">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <h2 className="flex items-center gap-3 font-display text-2xl font-medium">
          <Users className="h-5 w-5 text-amber-500" /> Comptes clients
        </h2>
        {clients.length > 0 && (
          <div className="flex gap-3">
            <button
              onClick={copyAllEmails}
              data-testid="clients-copy-emails"
              className="flex items-center gap-2 border border-amber-500/50 px-4 py-2.5 text-[10px] font-bold uppercase tracking-[0.2em] text-amber-400 transition-colors hover:bg-amber-500 hover:text-black"
            >
              <Copy className="h-3.5 w-3.5" /> Copier les emails
            </button>
            <a
              href={`${API}/admin/clients/export`}
              data-testid="clients-export-csv"
              className="flex items-center gap-2 border border-white/15 px-4 py-2.5 text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-300 transition-colors hover:border-amber-500 hover:text-amber-400"
            >
              <FileDown className="h-3.5 w-3.5" /> Export CSV
            </a>
          </div>
        )}
      </div>
      {clients.length === 0 ? (
        <p className="mt-5 text-sm text-zinc-500" data-testid="clients-empty">Aucun client inscrit pour le moment.</p>
      ) : (
        <div className="mt-6 overflow-x-auto">
          <table className="w-full text-left text-xs" data-testid="clients-table">
            <thead>
              <tr className="border-b border-white/10 text-[10px] uppercase tracking-[0.2em] text-zinc-500">
                <th className="py-2 pr-4">Client</th>
                <th className="py-2 pr-4">Contact</th>
                <th className="py-2 pr-4">Inscrit le</th>
                <th className="py-2 pr-4 text-right">Achats</th>
                <th className="py-2 text-right">Total dépensé</th>
                <th className="py-2 pl-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {clients.map((c) => (
                <tr key={c.user_id} className="border-b border-white/[0.05] text-zinc-300" data-testid={`client-${c.user_id}`}>
                  <td className="py-3 pr-4 font-medium">{c.name || "—"}</td>
                  <td className="py-3 pr-4">
                    <a
                      href={`mailto:${c.email}?subject=${encodeURIComponent("La Console des Artistes")}`}
                      data-testid={`client-mail-${c.user_id}`}
                      className="flex items-center gap-2 text-amber-400 underline-offset-4 transition-colors hover:underline"
                    >
                      <Mail className="h-3.5 w-3.5" /> {c.email}
                    </a>
                  </td>
                  <td className="py-3 pr-4 text-zinc-500">
                    {c.created_at ? new Date(c.created_at).toLocaleDateString("fr-FR") : "—"}
                  </td>
                  <td className="py-3 pr-4 text-right">{c.purchases_count}</td>
                  <td className="py-3 text-right font-bold text-amber-400">{eur(c.total_spent)}</td>
                  <td className="py-3 pl-4 text-right">
                    <button
                      onClick={() => deleteClient(c)}
                      data-testid={`client-delete-${c.user_id}`}
                      className="inline-flex h-8 w-8 items-center justify-center text-zinc-500 transition-colors hover:text-red-400"
                      title="Supprimer ce compte"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
};
